import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Group } from "@mantine/core";
import "../Admin/Event/AdminEvent.css";

interface DrinkStatusDto {
    functionDrinks: number;
    personalDrinks: number;
    ticketDrinks?: number;
    totalAllowed: number;
    usedDrinks: number;
    remainingDrinks: number;
}

interface PersonDto {
    personId: number;
    firstName?: string;
    lastName?: string;
    fullName?: string;
}

// matcher backend enum: Beer=1 Water=2 Wine=3
enum DrinkType {
    Beer = 1,
    Water = 2,
    Wine = 3,
}

type Params = {
    eventId?: string;
    personId?: string;        // ved /drinks/person/...
    ticketPersonId?: string;  // ved /drinks/ticket/...
};

const FormDrinkOrder: React.FC = () => {
    const { eventId, personId, ticketPersonId } = useParams<Params>();

    const parsedEventId = Number(eventId);
    const parsedPersonId = Number(personId);
    const parsedTicketPersonId = Number(ticketPersonId);

    const isTicket = useMemo(() => {
        return !isNaN(parsedTicketPersonId) && parsedTicketPersonId > 0;
    }, [parsedTicketPersonId]);

    const isPerson = useMemo(() => {
        return !isNaN(parsedPersonId) && parsedPersonId > 0;
    }, [parsedPersonId]);

    const [status, setStatus] = useState<DrinkStatusDto | null>(null);
    const [displayName, setDisplayName] = useState<string>("");

    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // ---------------- HENT PERSON NAVN (kun hvis person) ----------------
    const fetchPersonName = async () => {
        if (!parsedEventId || !isPerson) return;

        try {
            const res = await fetch(`/api/events/${parsedEventId}/persons`);
            if (!res.ok) return;

            const persons: PersonDto[] = await res.json();
            const person = persons.find((p) => p.personId === parsedPersonId);
            if (!person) return;

            const name =
                person.fullName ||
                `${person.firstName || ""} ${person.lastName || ""}`.trim();

            setDisplayName(name || `Person ${parsedPersonId}`);
        } catch {
            // ignore
        }
    };

    // ---------------- HENT DISPLAY NAVN (ticket) ----------------
    const setTicketName = () => {
        if (isTicket) setDisplayName(`Billet ${parsedTicketPersonId}`);
    };

    // ---------------- HENT STATUS ----------------
    const fetchStatus = async () => {
        setError(null);

        if (!parsedEventId || (!isPerson && !isTicket)) {
            setError("Ugyldig URL – mangler eventId + personId eller ticketPersonId");
            return;
        }

        const qs = new URLSearchParams();
        qs.set("eventId", String(parsedEventId));
        if (isPerson) qs.set("personId", String(parsedPersonId));
        if (isTicket) qs.set("ticketPersonId", String(parsedTicketPersonId));

        try {
            const res = await fetch(`/api/drinks/status?${qs.toString()}`);
            if (!res.ok) {
                const t = await res.text();
                throw new Error(t || "Kunne ikke hente status");
            }
            const data = await res.json();
            setStatus(data);
        } catch (e: any) {
            setError(e.message || "Ukendt fejl");
            setStatus(null);
        }
    };

    // ---------------- BESTIL DRINK ----------------
    const orderDrink = async (drinkType: DrinkType) => {
        setError(null);
        setSuccess(null);

        if (!status) return;

        // klient-side guard (så vi ikke sender hvis 0 tilbage)
        if (status.remainingDrinks <= 0) {
            setError("Ingen drinks tilbage");
            return;
        }

        if (!parsedEventId || (!isPerson && !isTicket)) {
            setError("Ugyldig URL");
            return;
        }

        const payload: any = {
            eventId: parsedEventId,
            drinkType, // tal 1/2/3
        };

        if (isPerson) payload.personId = parsedPersonId;
        if (isTicket) payload.ticketPersonId = parsedTicketPersonId;

        try {
            const res = await fetch("/api/drinks/order", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Ingen drinks tilbage");
            }

            setSuccess("✅ Drink bestilt!");
            await fetchStatus();
        } catch (e: any) {
            setError(e.message || "Ukendt fejl");
        }
    };

    // ---------------- LOAD ----------------
    useEffect(() => {
        setSuccess(null);
        setError(null);

        if (!parsedEventId) return;

        if (isTicket) {
            setTicketName();
        } else if (isPerson) {
            fetchPersonName();
        }

        fetchStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parsedEventId, parsedPersonId, parsedTicketPersonId]);

    return (
        <div className="form-container">
            <h2>
                Hej {displayName || (isTicket ? `Billet ${parsedTicketPersonId}` : `Person ${parsedPersonId}`)},
                her kan du vælge at bestille drinks
            </h2>

            {error && <p className="error-message">{error}</p>}
            {success && <p className="success-message">{success}</p>}

            {status && (
                <>
                    <div style={{ marginBottom: "15px" }}>
                        <div>Din function: {status.functionDrinks ?? 0}</div>
                        <div>Personlige: {status.personalDrinks ?? 0}</div>
                        {"ticketDrinks" in status && (
                            <div>Billet: {status.ticketDrinks ?? 0}</div>
                        )}
                        <div>Total: {status.totalAllowed ?? 0}</div>
                        <div>Brugt: {status.usedDrinks ?? 0}</div>
                        <div style={{ fontWeight: "bold" }}>
                            Tilbage: {status.remainingDrinks ?? 0}
                        </div>
                    </div>

                    <Group gap="sm">
                        <Button
                            disabled={status.remainingDrinks <= 0}
                            onClick={() => orderDrink(DrinkType.Beer)}
                        >
                            🍺 Øl
                        </Button>

                        <Button
                            disabled={status.remainingDrinks <= 0}
                            onClick={() => orderDrink(DrinkType.Water)}
                        >
                            💧 / 🥤 Vand / Sodavand
                        </Button>

                        <Button
                            disabled={status.remainingDrinks <= 0}
                            onClick={() => orderDrink(DrinkType.Wine)}
                        >
                            🍷 Vin
                        </Button>
                    </Group>

                    <Button
                        variant="light"
                        style={{ marginTop: 12 }}
                        onClick={fetchStatus}
                    >
                        Opdater status
                    </Button>
                </>
            )}
        </div>
    );
};

export default FormDrinkOrder;